import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type ReactNode } from 'react'

import {
  Button,
  EmptyState,
  InlineAlert,
  Input,
  Section,
  Select,
  Spinner,
  notify,
} from '@aeris/ui'

import {
  ApiError,
  fetchCouriers,
  saveCourier,
  type CourierView,
} from '../lib/api.ts'

/**
 * Rotas de entrega → Entregadores (F266): quem vai de moto, quem vai a pé, e o
 * WhatsApp de cada um (a F267 manda a rota para lá).
 */

const phoneText = (phone: string | null): string => {
  if (phone === null) return ''
  const local = phone.startsWith('55') ? phone.slice(2) : phone
  return local.length === 11
    ? `(${local.slice(0, 2)}) ${local.slice(2, 7)}-${local.slice(7)}`
    : local
}

const CourierRow = ({ courier }: { courier: CourierView }): ReactNode => {
  const queryClient = useQueryClient()
  const [mode, setMode] = useState(courier.mode)
  const [phone, setPhone] = useState(phoneText(courier.phone))
  const save = useMutation({
    mutationFn: async () =>
      await saveCourier(courier.partyId, {
        mode,
        phone: phone.trim() === '' ? null : phone,
        version: courier.version,
      }),
    onSuccess: (couriers) => {
      queryClient.setQueryData(['delivery-routes', 'couriers'], couriers)
      notify.success(`${courier.name} salvo.`)
    },
  })
  const changed = mode !== courier.mode || phone !== phoneText(courier.phone)

  return (
    <li className="flex flex-wrap items-end gap-3 py-2">
      <span className="min-w-40 flex-1 font-medium">{courier.name}</span>
      <Select
        aria-label={`Como ${courier.name} entrega`}
        value={mode}
        onChange={(event) => {
          setMode(event.target.value === 'foot' ? 'foot' : 'motorcycle')
        }}
        className="w-36"
      >
        <option value="motorcycle">De moto</option>
        <option value="foot">A pé</option>
      </Select>
      <Input
        aria-label={`WhatsApp de ${courier.name}`}
        placeholder="(33) 99999-0000"
        inputMode="tel"
        value={phone}
        onChange={(event) => {
          setPhone(event.target.value)
        }}
        className="w-44"
      />
      <Button
        size="sm"
        disabled={!changed || save.isPending}
        onClick={() => {
          save.mutate()
        }}
      >
        Salvar
      </Button>
      {save.isError ? (
        <InlineAlert tone="danger" className="w-full">
          {save.error instanceof ApiError
            ? save.error.message
            : 'Não foi possível agora.'}
        </InlineAlert>
      ) : null}
    </li>
  )
}

export const CouriersEditor = (): ReactNode => {
  const couriers = useQuery({
    queryKey: ['delivery-routes', 'couriers'],
    queryFn: async ({ signal }) => await fetchCouriers(signal),
  })
  const list = couriers.data ?? []
  return (
    <Section
      title="Entregadores"
      description="Quem tem o papel de entregador no cadastro. Diga quem vai de moto e quem vai a pé, e o WhatsApp para receber a rota."
    >
      {couriers.isPending ? (
        <Spinner label="Carregando os entregadores" />
      ) : list.length === 0 ? (
        <EmptyState
          title="Ninguém é entregador ainda"
          description="Em Clientes, abra a pessoa e marque o papel de entregador."
        />
      ) : (
        <ul className="flex flex-col divide-y divide-(--border-subtle)">
          {list.map((courier) => (
            <CourierRow
              key={`${courier.partyId}-${String(courier.version)}`}
              courier={courier}
            />
          ))}
        </ul>
      )}
    </Section>
  )
}
